import React, { useEffect, useState } from "react";
import { Copy, X, Plane, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useSearchParams } from "next/navigation";
import { useGetWebsiteConfig } from "@/hooks/use-configuration";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import { formatDateAndTime, formatDuration } from "@/lib/internalApis";

// Add this helper function near the top of the file
export const formatDate = (date: string) => {
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

function ViewDialog() {
  const { viewData } = useFlightBookingStore();
  const searchParams = useSearchParams();
  const { data: websiteConfig } = useGetWebsiteConfig();
  const getParam = (key: string) => searchParams?.get(key) || "";
  const queryData = JSON.parse(getParam("data") || "{}");
  const queryMetaData = JSON.parse(getParam("metadata") || "{}");
  const queryTripType = getParam("tripType");
  const [showDialog, setShowDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [copied, setCopied] = useState(false);



  useEffect(() => {
    if (viewData.length > 0) {
      setShowAlert(false);
    }
  }, [viewData])




  const handleOpenClick = () => {
    if (viewData.length === 0) {
      setShowDialog(false);
      setShowAlert(true);
    } else {
      setShowDialog(!showDialog);
    }
  };

  const generateCopyText = () => {
    let text = `Hello, please find details with regards to your flights(s) query for:\n`;

    queryMetaData?.forEach((route: any) => {
      text += `${route.fromCityOrAirport} - ${route.toCityOrAirport}\n`;
    });

    Object.keys(queryData?.paxInfo || {}).forEach((key: any) => {
      if (queryData?.paxInfo[key] !== "0") {
        text += `${queryData?.paxInfo[key]} ${key}\n`;
      }
    });

    text += `${queryData?.cabinClass}\n\n`;

    viewData.forEach((data: any) => {
      let { sI } = data;

      sI = sI.reduce(
        (acc: any, item: any) => {
          if (item.isRs) {
            acc.ret.push(item);
          } else {
            acc.dep.push(item);
          }
          return acc;
        },
        { dep: [], ret: [] }
      );

      let { dep, ret } = sI;

      const firstDep = dep[0];
      const lastDep = dep[dep?.length - 1];

      const depTotalDuration =
        dep.length < 2
          ? dep[0]?.duration
          : firstDep?.duration + (lastDep?.duration ?? 0) + (firstDep.cT ?? 0);

      const firstRet = ret[0];
      const lastRet = ret[ret?.length - 1];
      const retTotalDuration =
        ret.length < 2
          ? ret[0]?.duration
          : firstRet?.duration + (lastRet?.duration ?? 0) + (firstRet.cT ?? 0);

      // Departure details
      if (dep.length > 0) {
        text += `${data.tripType} (Departure)\n`;
        text += `${dep[0].fD.aI.name} (${dep
          .map((s: any) => s.fD.fN)
          .join(", ")})\n`;
        text += `${dep[0].da.cityCode ?? dep[0].da.code}(${dep[0].da.city}) - ${dep[dep.length - 1].aa.cityCode ?? dep[dep.length - 1].aa.code
          }(${dep[dep.length - 1].aa.city}) ${dep
            .map((s: any, index: number) =>
              index > 0 && index < data.sI.length
                ? `via ${s.da.cityCode ?? s.da.code}(${s.da.city})`
                : ""
            )
            .filter(Boolean)
            .join(", ")}\n`;
        text += `Departure: ${formatDate(dep[0].dt)}\n`;
        text += `Arrival: ${formatDate(dep[dep.length - 1].at)}\n`;
        text += `Duration: ${formatDuration(depTotalDuration)} / ${dep.length > 1
          ? `${dep.length - 1} Stop${dep.length - 1 > 1 ? "s" : ""}`
          : "Non Stop"
          }\n`;
      }

      // Return details
      if (ret.length > 0) {
        text += `${data.tripType} (Return)\n`;
        text += `${ret[0].fD.aI.name} (${ret
          .map((s: any) => s.fD.fN)
          .join(", ")})\n`;
        text += `${ret[0].da.cityCode ?? ret[0].da.code}(${ret[0].da.city}) - ${ret[ret.length - 1].aa.cityCode ?? ret[ret.length - 1].aa.code
          }(${ret[ret.length - 1].aa.city}) ${ret
            .map((s: any, index: number) =>
              index > 0 && index < data.sI.length
                ? `via ${s.da.cityCode ?? s.da.code}(${s.da.city})`
                : ""
            )
            .filter(Boolean)
            .join(", ")}\n`;
        text += `Departure: ${formatDate(ret[0].dt)}\n`;
        text += `Arrival: ${formatDate(ret[ret.length - 1].at)}\n`;
        text += `Duration: ${formatDuration(retTotalDuration)} / ${ret.length > 1
          ? `${ret.length - 1} Stop${ret.length - 1 > 1 ? "s" : ""}`
          : "Non Stop"
          }\n`;
      }

      if (data.fare) {
        Object.keys(data?.fare?.fd).forEach((key: any) => {
          text += `${key}: ${data.fare.fd[key].bI.iB} | ${data.fare.fd[key].bI.cB
            }${data.fare.fd[key].mI ? " / Free Meal" : ""}\n`;
        });
      }
      text += `Price: ${data.totalPrice}\n\n`;
    });

    text += `Thank you for choosing ${websiteConfig?.businessName}\n`;
    text += `In case of any support:\n`;
    text += `☎️ Contact: ${websiteConfig?.customerSupport.mobile}\n`;
    text += `✉ Email: ${websiteConfig?.customerSupport.email}\n\n`;
    text += `Airline ticket pricing is dynamic. Fares are valid as of now and might change at the time of issuance.`;


    return text;
  };


  const handleCopy = () => {
    const text = generateCopyText();
    if (navigator.clipboard && window.isSecureContext) {
      // Use Clipboard API if available and in secure context
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };


  return (
    <>
      {showAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <Alert className="bg-white shadow-lg">
            <AlertTitle className="flex items-center justify-between">
              No Flights Available
              <button onClick={() => setShowAlert(false)}>
                <X />
              </button>
            </AlertTitle>
            <AlertDescription>
              There are no flight details available for your query.
            </AlertDescription>
          </Alert>
        </div>
      )}
      <Dialog open={showDialog} onOpenChange={handleOpenClick}>
        <DialogTrigger asChild>
          <button onClick={handleOpenClick} className="mr-3">
            Open
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader className="flex flex-row justify-between items-center border-b pb-4 pt-2">
            <DialogTitle className="text-xl font-semibold">
              View Details
            </DialogTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                className={`${copied ? "bg-green-50" : ""} transition-colors`}
                onClick={handleCopy}
              >
                {copied ? (<CheckCheck className="h-4 w-4 text-green-600" />) : (<Copy className={`h-4 w-4`} />)}
              </Button>
              {/* <Button
                variant="outline"
                size="icon"
                onClick={() => setShowDialog(false)}
              >
                <X className="h-4 w-4" />
              </Button> */}
            </div>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <p className="text-gray-600">
                Hello, please find details with regards to your flights(s) query
                for:
              </p>
              {queryMetaData?.map((route: any, index: number) => (
                <p key={index} className="font-medium">
                  {route.fromCityOrAirport} - {route.toCityOrAirport}
                </p>
              ))}
              <div className="space-x-2">
                {Object.keys(queryData?.paxInfo || {}).map(
                  (key: any) =>
                    queryData?.paxInfo[key] !== "0" && (
                      <span key={key} className="text-sm text-gray-600">
                        {queryData?.paxInfo[key]} {key}
                      </span>
                    )
                )}
                <span className="text-sm text-gray-600">
                  {queryData?.cabinClass}
                </span>
              </div>
            </div>

            <p className="text-gray-600 border-t pt-4">
              Below mentioned prices are the total price(s) inclusive of taxes:{" "}
              {viewData.length}
            </p>

            <div className="space-y-6">
              {viewData.length > 0 &&
                viewData?.map((data: any, index: number) => {
                  let { sI } = data;
                  sI = sI.reduce(
                    (acc: any, item: any) => {
                      if (item.isRs) {
                        acc.ret.push(item);
                      } else {
                        acc.dep.push(item);
                      }
                      return acc;
                    },
                    { dep: [], ret: [] }
                  );

                  let { dep, ret } = sI;

                  const firstDep = dep[0];
                  const lastDep = dep[dep?.length - 1];

                  const depTotalDuration =
                    dep.length < 2
                      ? dep[0]?.duration
                      : firstDep?.duration +
                      (lastDep?.duration ?? 0) +
                      (firstDep.cT ?? 0);

                  const firstRet = ret[0];
                  const lastRet = ret[ret?.length - 1];
                  const retTotalDuration =
                    ret.length < 2
                      ? ret[0]?.duration
                      : firstRet?.duration +
                      (lastRet?.duration ?? 0) +
                      (firstRet.cT ?? 0);

                  return (
                    <div
                      key={index}
                      className="border rounded-lg p-4 space-y-2"
                    >
                      {/* departure */}
                      {dep.length > 0 && (
                        <div>
                          <p className="font-medium text-blue-600">
                            {data.tripType === "Combo"
                              ? queryTripType === "multicity"
                                ? "Multi-City"
                                : "ONEWORD"
                              : data.tripType}
                          </p>
                          <div className="flex items-center gap-2">
                            <Plane className="h-5 w-5 text-gray-500" />
                            <p className="text-sm">
                              {dep[0].fD.aI.name} (
                              {dep.map((s: any) => s.fD.fN).join(", ")})
                            </p>
                          </div>
                          <p className="text-sm">
                            {dep[0].da.cityCode ?? dep[0].da.code}({dep[0].da.city}) -{" "}
                            {dep[dep.length - 1].aa.cityCode ?? dep[dep.length - 1].aa.code}(
                            {dep[dep.length - 1].aa.city})
                            {dep.length > 1 && (
                              <span className="text-gray-500">
                                {" "}
                                via{" "}
                                {dep
                                  .map((s: any, index: number) =>
                                    index > 0 && index < data.sI.length
                                      ? `${s.da.cityCode}(${s.da.city})`
                                      : ""
                                  )
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            )}
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <p>
                              Departure:{" "}
                              <span className="text-gray-600">
                                {formatDate(dep[0]?.dt)}
                              </span>
                            </p>
                            <p>
                              Arrival:{" "}
                              <span className="text-gray-600">
                                {formatDate(dep[dep?.length - 1]?.at)}
                              </span>
                            </p>
                          </div>
                          <p className="text-sm">
                            Duration: {formatDuration(depTotalDuration)} /
                            {dep.length > 1
                              ? `${dep.length - 1} Stop${dep.length - 1 > 1 ? "s" : ""
                              }`
                              : "Non Stop"}
                          </p>
                          {data?.fare && Object.keys(data?.fare?.fd).map((key: any) => (
                            <p key={key} className="text-sm">
                              {key}: {data.fare.fd[key].bI.iB} |{" "}
                              {data?.fare?.fd[key]?.bI?.cB}
                              {data?.fare?.fd[key]?.mI && " / Free Meal"}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Return */}
                      {ret.length > 0 && (
                        <div>
                          <p className="font-medium text-blue-600">
                            {data.tripType === "Combo"
                              ? queryTripType === "multicity"
                                ? "Multi-City"
                                : "RETURN"
                              : data.tripType}
                          </p>
                          <div className="flex items-center gap-2">
                            <Plane className="h-5 w-5 text-gray-500" />
                            <p className="text-sm">
                              {ret[0].fD.aI.name} (
                              {ret.map((s: any) => s.fD.fN).join(", ")})
                            </p>
                          </div>
                          <p className="text-sm">
                            {ret[0].da.cityCode}({ret[0].da.city}) -{" "}
                            {ret[ret.length - 1].aa.cityCode}(
                            {ret[ret.length - 1].aa.city})
                            {ret.length > 1 && (
                              <span className="text-gray-500">
                                {" "}
                                via{" "}
                                {ret
                                  .map((s: any, index: number) =>
                                    index > 0 && index < data.sI.length
                                      ? `${s.da.cityCode}(${s.da.city})`
                                      : ""
                                  )
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            )}
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <p>
                              Departure:{" "}
                              <span className="text-gray-600">
                                {formatDate(ret[0].dt)}
                              </span>
                            </p>
                            <p>
                              Arrival:{" "}
                              <span className="text-gray-600">
                                {formatDate(ret[ret?.length - 1].at)}
                              </span>
                            </p>
                          </div>
                          <p className="text-sm">
                            Duration: {formatDuration(retTotalDuration)} /
                            {ret.length > 1
                              ? `${ret.length - 1} Stop${ret.length - 1 > 1 ? "s" : ""
                              }`
                              : "Non Stop"}
                          </p>
                          {data?.fare && Object.keys(data.fare.fd).map((key: any) => (
                            <p key={key} className="text-sm">
                              {key}: {data.fare.fd[key].bI.iB} |{" "}
                              {data.fare.fd[key].bI.cB}
                              {data.fare.fd[key].mI && " / Free Meal"}
                            </p>
                          ))}
                        </div>
                      )}

                      {/*  */}

                      <p className="font-medium">Price: {data.totalPrice}</p>
                    </div>
                  );
                })}
            </div>

            <div className="border-t pt-4 space-y-2 text-sm text-gray-600">
              <p>Thank you for choosing {websiteConfig?.businessName}</p>
              <p>In case of any support:</p>
              <p>☎️ Contact: {websiteConfig?.customerSupport.mobile}</p>
              <p>✉ Email: {websiteConfig?.customerSupport.email}</p>
              <p className="italic">
                Airline ticket pricing is dynamic. Fares are valid as of now and
                might change at the time of issuance.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ViewDialog;
